import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ShieldCheck } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { DownloadButton } from '@/components/marketing/DownloadButton';
import { RouteHead } from '@/components/seo/RouteHead';
import { CHANNELS, PLATFORMS } from '@/lib/db';
import { fadeIn, fadeUp, staggerContainer, STAGGER, VIEWPORT_REVEAL } from '@/lib/motion';
import { useLatestRelease } from '@/lib/releases';
import { ROUTES } from '@/routes';
import { ChecksumCard } from './ChecksumCard';
import { RequirementList } from './RequirementList';
import { buildUrls, channelI18nKey, formatReleasedAt } from './helpers';

export default function DownloadPage() {
  const { t, i18n } = useTranslation('download');
  const { data: release, isPending, isError } = useLatestRelease(CHANNELS.stable);

  const hasAnyHash = !!(release && (release.windowsSha256 || release.macosSha256));
  const winRequirements = [
    t('requirements.windows_item_1'),
    t('requirements.windows_item_2'),
    t('requirements.windows_item_3'),
  ] as const;
  const macRequirements = [
    t('requirements.macos_item_1'),
    t('requirements.macos_item_2'),
    t('requirements.macos_item_3'),
    t('requirements.macos_item_4'),
  ] as const;

  return (
    <>
      <RouteHead
        path={ROUTES.download}
        titleKey="seo:download.title"
        descriptionKey="seo:download.description"
      />
      {/* Hero */}
      <Section padding="lg">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fadeIn}
              className="mb-4 text-xs font-semibold uppercase tracking-widest text-brand"
            >
              {t('page.title')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t('page.headline')}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              {t('page.subheadline')}
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10">
              {isPending ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="text-sm text-muted-foreground"
                >
                  {t('page.loading')}
                </p>
              ) : isError || !release ? (
                <div role="alert" className="mx-auto max-w-md text-center">
                  <p className="font-semibold">{t('page.error_headline')}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t('page.error_body')}</p>
                </div>
              ) : (
                <>
                  <DownloadButton urls={buildUrls(release)} version={release.version} />
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
                    <span className="rounded-full border border-border bg-card px-3 py-1 font-medium">
                      {t(channelI18nKey(release.channel))}
                    </span>
                    <span className="font-mono text-fg-subtle">
                      {t('page.released_label', {
                        date: formatReleasedAt(release.releasedAt, i18n.language),
                      })}
                    </span>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Checksums */}
      <Section padding="lg" bordered>
        <Container size="md">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
            className="flex flex-col gap-8"
          >
            <motion.div variants={fadeUp} className="text-center">
              <Heading level={2}>{t('checksum.headline')}</Heading>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
                {t('checksum.subheadline')}
              </p>
            </motion.div>

            {release && hasAnyHash ? (
              <div className="grid gap-6 md:grid-cols-2">
                <ChecksumCard release={release} platform={PLATFORMS.windows} />
                <ChecksumCard release={release} platform={PLATFORMS.macos} />
              </div>
            ) : (
              <motion.div
                variants={fadeUp}
                className="mx-auto flex max-w-xl flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card/50 p-6 text-center"
              >
                <ShieldCheck className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                <p className="font-semibold">{t('checksum.unavailable_headline')}</p>
                <p className="text-sm text-muted-foreground">{t('checksum.unavailable_body')}</p>
              </motion.div>
            )}
          </motion.div>
        </Container>
      </Section>

      {/* Requirements */}
      <Section padding="lg" bordered>
        <Container size="md">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
            className="flex flex-col gap-8"
          >
            <motion.div variants={fadeUp} className="text-center">
              <Heading level={2}>{t('requirements.headline')}</Heading>
            </motion.div>
            <div className="grid gap-6 md:grid-cols-2">
              <RequirementList
                headline={t('requirements.windows_headline')}
                items={winRequirements}
                icon={PLATFORMS.windows}
              />
              <RequirementList
                headline={t('requirements.macos_headline')}
                items={macRequirements}
                icon={PLATFORMS.macos}
              />
            </div>
          </motion.div>
        </Container>
      </Section>

      {/* Changelog link */}
      <Section padding="lg" className="bg-primary-soft">
        <Container size="sm" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('links.changelog_headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-3 max-w-md text-muted-foreground">
              {t('links.changelog_body')}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-6">
              <Link
                to={ROUTES.changelog}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors duration-fast ease-out-quart hover:border-brand/50 hover:text-brand"
              >
                {t('links.changelog_cta')}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </motion.div>
          </motion.div>
        </Container>
      </Section>
    </>
  );
}
